import { Card, CardContent, Typography, Box, LinearProgress } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import styles from "./RatingDistribution.module.scss";

const RATINGS = [5, 4, 3, 2, 1];
const COLORS = ["#34A853", "#7CB342", "#FBC02D", "#FB8C00", "#E53935"];
const BG_RING = "#eee";

const RatingDistribution = ({ data = [], averageRating = 0 }) => {
    const countsByRating = new Map(data.map(d => [Number(d.rating), Number(d.count) || 0]));
    const chartData = RATINGS.map((r) => ({ name: r.toString(), value: countsByRating.get(r) || 0 }));

    const total = chartData.reduce((sum, d) => sum + d.value, 0);
    const percentData = chartData.map((d) => ({
        ...d,
        percent: total ? (d.value / total) * 100 : 0,
    }));

    return (
        <Card className={styles.card}>
            <CardContent className={styles.cardContent}>
                <Typography variant="subtitle3" className={styles.title}>
                    Rating Distribution
                </Typography>

                <div className={styles.chartContainer}>
                    <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                            {/* Background ring */}
                            <Pie data={[{ name: "bg", value: 100 }]} innerRadius={50} outerRadius={82} dataKey="value" isAnimationActive={false}>
                                <Cell fill={BG_RING} />
                            </Pie>

                            {/* Actual ratings */}
                            <Pie
                                data={percentData}
                                innerRadius={54}
                                outerRadius={108}
                                paddingAngle={0.4}
                                dataKey="value"
                                isAnimationActive={false}
                                strokeWidth={0.4}
                            >
                                {percentData.map((_, i) => (
                                    <Cell key={`cell-${i}`} fill={COLORS[i]} />
                                ))}
                            </Pie>

                            <Tooltip
                                cursor={false}
                                content={({ active, payload }) => {
                                    if (!active || !payload?.length) return null;

                                    const valid = payload.find(
                                        (p) => p?.payload && /^[1-5]$/.test(p.payload.name)
                                    );
                                    if (!valid) return null;

                                    const rating = Number(valid.payload.name);
                                    const count = countsByRating.get(rating) || 0;
                                    const percent = valid.payload.percent?.toFixed(0) || 0;

                                    return (
                                        <div className={styles.tooltipBox}>
                                            <strong>{rating} Stars</strong>: {count} ({percent}%)
                                        </div>
                                    );
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>

                    <div className={styles.centerLabel}>
                        {total === 0 ? "-" : Number(averageRating).toFixed(1)}
                    </div>
                </div>

                {percentData.map((item, i) => (
                    <div key={item.name} className={styles.ratingRow}>
                        <Typography variant="body2" className={styles.ratingLabel}>
                            {item.name}
                        </Typography>
                        <StarRoundedIcon className={styles.starIcon} />
                        <div className={styles.progressWrapper}>
                            <LinearProgress
                                variant="determinate"
                                value={item.percent}
                                className={styles.linearProgress}
                                sx={{
                                    height: 12,
                                    borderRadius: 8,
                                    "& .MuiLinearProgress-bar": {
                                        backgroundColor: COLORS[i],
                                        borderRadius: 8,
                                    },
                                }}
                            />
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};

export default RatingDistribution;
